import "@popperjs/core";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap";
import "./assets/css/style.css";
import "semantic-ui-css/semantic.min.css";

import { LoadingPlugin } from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import DOMPurify from "dompurify";

import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "./axios";

import globalMixin from "@/mixins/globalMixin";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";

(function checkSSOOnLoad() {
  const now = Date.now();

  const currentPath = window.location.pathname;
  const excludedPaths = [
    "/verify-sso",
    "/verify-token",
    "/forgot",
    "/forbidden",
    "/reset-password",
    "/confirm-email",
    "/tarana-resources",
    "/corporate-resources",
    "/operators",
    "/doc/",
  ];
  const isExcluded = excludedPaths.some((path) => currentPath.startsWith(path));

  const user = localStorage.getItem("user");
  const sso = localStorage.getItem("sso");
  const ssoCheck = localStorage.getItem("sso-check");

  const isSSOUser = user && sso;
  const isLoggedOut = !user;
  const isNotLocalhost =
    process.env.VUE_APP_MAIN_URL !== "http://localhost:8080/";

  const isExpired = ssoCheck != "checked";

  const shouldCheck =
    (isSSOUser || isLoggedOut) && isExpired && !isExcluded && isNotLocalhost;

  if (shouldCheck) {
    localStorage.setItem("sso-check-date", now.toString());
    window.location.href =
      process.env.VUE_APP_BASE_URL.replace("/api", "") + "sso-silent-check/web";
    return;
  }

  // This should only clear the check if SSO is valid
  if (!isExpired) {
    localStorage.removeItem("sso-check");
  }
})();

// Define the click-outside directive
const clickOutsideDirective = {
  beforeMount(el, binding) {
    el.clickOutsideEvent = function (event) {
      // Check if click was outside the element and its children
      if (
        !(el == event.target || el.contains(event.target)) &&
        !event.target.closest(".sidebar")
      ) {
        // Execute the provided method
        binding.value(event);
      }
    };
    document.body.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted(el) {
    document.body.removeEventListener("click", el.clickOutsideEvent);
  },
};

store.dispatch("auth/initializeApp");
library.add(fas, fab, far);
const app = createApp(App);
app.config.globalProperties.$axios = axios;

// security/2.1: global HTML sanitizer for every `v-html` binding in the app.
// Every template that renders admin-authored or server-supplied HTML must call
// `$sanitize(...)` around the value, e.g. `v-html="$sanitize(resource.description)"`.
// DOMPurify strips <script>, event handlers (on*), javascript:/data: URLs, and
// any tag/attr outside the allow-list below. The allow-list is intentionally
// permissive to support existing admin-authored content (headings, images,
// code blocks, tables); tighten if content review shows unused tags.
//
// Do NOT introduce a new `v-html` without wrapping it in `$sanitize(...)`.
// Server-side sanitization is tracked as BACKLOG BL-014 (defense-in-depth;
// deferred behind more pressing items).
app.config.globalProperties.$sanitize = (html) =>
  DOMPurify.sanitize(html ?? "", {
    ALLOWED_TAGS: [
      "p", "br", "hr", "b", "i", "em", "strong", "u", "a",
      "ul", "ol", "li",
      "h1", "h2", "h3", "h4", "h5", "h6",
      "img", "blockquote", "code", "pre",
      "table", "thead", "tbody", "tr", "td", "th",
      "span", "div",
    ],
    ALLOWED_ATTR: ["href", "target", "rel", "src", "alt", "title", "class"],
  });
app.use(LoadingPlugin);
app.use(router);
app.use(store);
app.mixin(globalMixin);
app.component("font-awesome-icon", FontAwesomeIcon);
app.component("v-select", vSelect);

// Register the directive globally
app.directive("click-outside", clickOutsideDirective);

app.mount("#app");
